import { BPS_ONE } from '../damage/exact';

/**
 * **A métrica canónica do casco: quanto ele MEDE, dada a vida máxima que tem.**
 *
 * ## O problema que este ficheiro resolve
 *
 * Antes deste gate havia duas geometrias e nenhuma delas era autoritativa. O
 * duelo desenhava uma escala no cliente e colidia contra uma constante no
 * servidor - a aparência crescia e a caixa de colisão não - e a caça desenhava a
 * nave a 96 unidades fixas, com as serras a orbitarem metade disso.
 *
 * Uma aparência que cresce enquanto a física não cresce é o desenho a mentir. E
 * duas fórmulas em dois módulos são duas fórmulas que divergem.
 *
 * Então há UM número - a escala do casco - e tudo o que está preso ao casco sai
 * dele: o sprite, a hitbox, o raio autoritativo, a colisão de projéteis, a do
 * Míssil, a proximidade da Bomba, o contacto e a órbita das Serras, o ponto onde
 * um tiro nasce, as paredes, a barra de vida, o marcador de equipa, as marcas de
 * Veneno e de Sangramento, e os números de dano e de cura.
 *
 * ## A curva é uma TABELA DE MARCOS, e foi decidida a olhar para o jogo
 *
 * Sete marcos e uma reta entre cada dois - a **curva C**, aprovada pelo dono no
 * gate V28I.CF.4. Ver {@link HULL_SCALE_MILESTONES}.
 *
 * Ela já foi duas outras coisas, e as duas falharam por pontas opostas:
 *
 * - **A raiz quadrada**, que é a resposta clássica porque a vida se lê como
 *   ÁREA. Montada numa bancada com as duas naves lado a lado e **medida**:
 *   `sqrt(1,6)` são `+26,5 %` de largura, dez píxeis ao zoom padrão. O dono olhou
 *   e a diferença não se lia.
 * - **A proporção direta**, `vida / 100`, que a substituiu. Essa lia-se no
 *   início e mentia no fim: a auditoria do V28I.CF.4 mediu o topo do conteúdo
 *   liberado em **298 790** de vida, o que dava **2 988x** - um casco maior do
 *   que o quadrante que o contém.
 *
 * A curva C liga marcos da progressão REAL às escalas que o dono quer ver em
 * cada fase, e por isso lê-se de ponta a ponta: uma conta que sai do início para
 * o meio do jogo duplica de tamanho, e o topo do conteúdo liberado chega
 * exatamente ao teto.
 *
 * ## Há um TETO FÍSICO de 23x, e ele não é um teto de vida
 *
 * Ver {@link HULL_MAX_SCALE_BPS}. A vida máxima continua **sem limite nenhum**:
 * acima de 298 790 cada ponto continua a somar, o equipamento continua a
 * multiplicar e o casco continua a aguentar tudo o que a conta comprou. O que
 * para de crescer é o tamanho.
 *
 * Isto SUPERA a nota "sem tecto" do gate V28G.R, que tirou o `clamp` de 3x do
 * duelo. A razão daquela decisão continua válida e é respeitada aqui: o que não
 * pode ser capado é o ATRIBUTO, e nenhum atributo é capado.
 *
 * A escala é MONOTÓNICA em todo o domínio: mais vida máxima nunca é uma nave
 * menor, e nunca há degrau.
 *
 * **Dependência registada, e deliberadamente não feita neste gate:** a arena do
 * duelo tem de crescer substancialmente e perder os seus obstáculos, com as
 * próprias naves resistentes a servirem de obstáculo físico.
 * `arena-geometry.test.ts` prova que todo o corredor e toda a folga de parede
 * admitem `PVP_MAX_VISUAL_SCALE` cascos. A curva C **adia** esse limite em vez de
 * o antecipar - com a proporção direta ele chegava aos 3 000 de vida, e com esta
 * chega perto dos 27 000 - mas o teto de 23x continua acima do que a arena de
 * hoje admite, e a dependência continua aberta.
 *
 * ## Porque a aritmética é inteira
 *
 * Porque o resultado decide COLISÃO, e a colisão é re-simulada pelo servidor e
 * prevista pelo cliente. Uma divisão em vírgula flutuante poria as duas pontas a
 * um `mu` de distância uma da outra conforme o motor - o `CLAUDE.md` já regista
 * que Node e Chromium diferem no último bit de um `Math.atan2` - e uma nave que
 * colidisse num sítio diferente em cada máquina seria uma dessincronização que
 * ninguém consegue reproduzir.
 *
 * Tudo em `bigint`, com uma truncatura só. Duas máquinas dão o mesmo inteiro
 * sempre.
 */

/**
 * **O teto físico: 23,00x.** Decisão do dono (gate V28I.CH.11B, ADR 0138); era
 * 28,00x (ADR 0134).
 *
 * ## Porque um teto AQUI não é o teto que o `CLAUDE.md` proíbe
 *
 * O que o projeto proíbe é capar, dividir ou esconder o ATRIBUTO que um item
 * concede. Este número não capa vida nenhuma: a vida máxima continua a crescer
 * sem limite acima dos 298 790, os pontos continuam a somar, o equipamento
 * continua a multiplicar, e o casco continua a aguentar cada ponto que a conta
 * comprou. O que para de crescer é o TAMANHO FÍSICO - uma decisão de
 * apresentação e de física, tomada a olhar para o jogo numa bancada, como o
 * teto de cadência do campo foi.
 *
 * **Está antes dos marcos de propósito**: eles derivam dele, e uma constante
 * usada antes de ser declarada é um erro em tempo de execução, não de tipos.
 */
export const HULL_MAX_SCALE_BPS = 230_000n;

/**
 * **A FORMA da curva** (a curva C da ADR 0134): as vidas onde ela muda de
 * andamento, e as escalas que a decisão dos 28x lhes tinha dado.
 *
 * Ela não é a lei. A lei é esta forma com o eixo da escala remapeado para o
 * teto em vigor, logo abaixo — e é por isso que baixar o teto outra vez é mudar
 * UM número, e não redesenhar sete.
 */
const HULL_SCALE_SHAPE: readonly (readonly [bigint, bigint])[] = Object.freeze([
  [100_000n, 10_000n],
  [410_000n, 20_000n],
  [1_299_000n, 40_000n],
  [27_083_000n, 100_000n],
  [102_557_000n, 150_000n],
  [178_031_000n, 200_000n],
  [298_790_000n, 280_000n],
]);

/** O topo da FORMA, lido dela e nunca escrito: a escala do último marco. */
const HULL_SHAPE_TOP_BPS = (
  HULL_SCALE_SHAPE[HULL_SCALE_SHAPE.length - 1] as readonly [bigint, bigint]
)[1];

/**
 * **Os marcos em vigor**: a forma acima, com o eixo da ESCALA remapeado.
 *
 *     escala' = 1x + (escala - 1x) x (teto - 1x) / (topo da forma - 1x)
 *
 * É uma transformação afim crescente, e é isso que dá as quatro propriedades
 * que a decisão exige:
 *
 * - **`1x` continua exatamente `1x`** — o termo multiplicado é zero;
 * - **a vida terminal que dava o topo da forma dá o teto novo**, ao bps: com o
 *   teto em 230 000, `10 000 + 270 000 x 220 000 / 270 000` = 230 000;
 * - **monótona e sem degrau**, porque uma afim crescente preserva a ordem e a
 *   continuidade de cada troço, e a inversa lê esta mesma tabela;
 * - **sem platô antecipado**: o teto só é atingido na MESMA vida em que a forma
 *   atingia o dela. Um `min(forma, teto)` teria feito o contrário — achatava
 *   tudo acima de 16,48x e mentia sobre a progressão precisamente onde ela é
 *   mais cara.
 *
 * As VIDAS são as da auditoria da progressão real do gate V28I.CF.4 — contas que
 * o catálogo de hoje permite montar, e não números bonitos —, e as escalas são
 * as que o teto em vigor lhes dá:
 *
 * | vida máxima | escala | o que é essa conta |
 * | ---: | ---: | --- |
 * | 100 | 1,0000x | uma conta nova, sem um ponto, sem peça e sem joia |
 * | 410 | 1,8148x | cinco peças de nível 1, dezanove pontos |
 * | 1 299 | 3,4444x | peças de nível 5, artefactos de nível 1, joia de nível 10 |
 * | 27 083 | 8,3333x | peças de nível 10, artefactos de nível 5, joia de nível 20 |
 * | 102 557 | 12,4074x | |
 * | 178 031 | 16,4814x | |
 * | 298 790 | **23,0000x** | o topo do conteúdo liberado, medido e não estimado |
 */
export const HULL_SCALE_MILESTONES: readonly (readonly [bigint, bigint])[] = Object.freeze(
  HULL_SCALE_SHAPE.map(
    ([health, bps]) =>
      [
        health,
        BPS_ONE +
          ((bps - BPS_ONE) * (HULL_MAX_SCALE_BPS - BPS_ONE)) / (HULL_SHAPE_TOP_BPS - BPS_ONE),
      ] as const,
  ),
);

/**
 * **A lei, em basis points, e a ÚNICA.** `10 000` é a nave inicial, exatamente.
 *
 *     escalaBps = min( interpolação linear entre os marcos, HULL_MAX_SCALE_BPS )
 *
 * ## A curva foi escolhida a olhar para a progressão real
 *
 * Ela começou por ser a raiz quadrada, que é a resposta clássica - a vida lê-se
 * como ÁREA. Foi medida numa bancada e a diferença não se lia. Passou então a
 * ser a proporção direta, `vida / 100`, e essa mentia pela outra ponta: o topo do
 * conteúdo liberado dava **2 988x**, um casco do tamanho de um quadrante.
 *
 * A auditoria do V28I.CF.4 mediu a progressão que existe - base 100, ponto +10,
 * cinco peças até 33,52x no nível 10, os artefactos a dobrar por nível, a joia a
 * +60 % - e o dono escolheu ligar os marcos dessa progressão às escalas que quer
 * ver em cada fase. É por isso que a curva é uma TABELA e não uma fórmula
 * fechada: ela é uma decisão de conteúdo, e muda quando o conteúdo mudar.
 *
 * ## Contínua, e sem degrau nenhum
 *
 * Entre dois marcos a escala é a reta que os liga, portanto um ponto de vida
 * nunca muda o tamanho de repente. Nos marcos o resultado é EXATAMENTE o da
 * tabela, por construção: a interpolação de um extremo devolve o extremo.
 *
 * ## Porque em basis points e não como um `number`
 *
 * Porque o resultado decide COLISÃO, e a colisão é re-simulada pelo servidor e
 * prevista pelo cliente. Uma divisão em vírgula flutuante poria as duas pontas a
 * um `mu` de distância uma da outra conforme o motor - o `CLAUDE.md` já regista
 * que Node e Chromium diferem no último bit de um `Math.atan2` - e uma nave que
 * colidisse num sítio diferente em cada máquina seria uma dessincronização que
 * ninguém consegue reproduzir. Tudo em `bigint`, com uma truncatura por troço.
 *
 * ## Não recebe referência nenhuma
 *
 * Recebia, e a referência era sempre a mesma constante nos dois modos. Uma
 * tabela com vidas ABSOLUTAS não tem onde pôr uma segunda referência: 410 de
 * vida são 2x, e não "2x de qualquer coisa". O parâmetro saiu para que não possa
 * existir um segundo `1x` em lado nenhum.
 *
 * ## O piso
 *
 * Uma conta nova tem 100 e mede `1,00x`; nada desce daí. Uma vida abaixo da
 * inicial - que nenhuma fonte legal produz, mas um destroço a zero produz -
 * continua a medir `1,00x` em vez de desaparecer. O que se recusa é um valor
 * NEGATIVO, que não é um casco.
 */
export function hullScaleBps(maxHealthMilli: bigint): bigint {
  if (maxHealthMilli < 0n) {
    throw new RangeError(`a hull maximum health is never negative, got ${maxHealthMilli}`);
  }
  const first = HULL_SCALE_MILESTONES[0];
  const last = HULL_SCALE_MILESTONES[HULL_SCALE_MILESTONES.length - 1];
  if (first === undefined || last === undefined) {
    return BPS_ONE;
  }
  if (maxHealthMilli <= first[0]) {
    return first[1];
  }
  if (maxHealthMilli >= last[0]) {
    return HULL_MAX_SCALE_BPS;
  }
  for (let index = 1; index < HULL_SCALE_MILESTONES.length; index += 1) {
    const low = HULL_SCALE_MILESTONES[index - 1];
    const high = HULL_SCALE_MILESTONES[index];
    if (low === undefined || high === undefined) {
      continue;
    }
    if (maxHealthMilli <= high[0]) {
      /* Uma truncatura, e só uma, no fim do troço. */
      return low[1] + ((high[1] - low[1]) * (maxHealthMilli - low[0])) / (high[0] - low[0]);
    }
  }
  return HULL_MAX_SCALE_BPS;
}

/**
 * **Quanta vida esta curva pede para uma escala.** A MESMA tabela, lida ao
 * contrário - e nunca uma segunda fórmula.
 *
 * Serve a quem precisa de montar um casco de um tamanho conhecido: uma bancada,
 * uma folha visual, uma prova. Devolve a MENOR vida em milli que produz pelo
 * menos `targetBps`, e a vida terminal para qualquer pedido igual ou acima do
 * teto.
 *
 * Uma inversa escrita à mão seria uma segunda curva a divergir da primeira no
 * dia em que um marco mudasse; esta percorre os mesmos marcos e inverte a mesma
 * reta de cada troço.
 */
export function hullHealthMilliForScaleBps(targetBps: bigint): bigint {
  const first = HULL_SCALE_MILESTONES[0];
  const last = HULL_SCALE_MILESTONES[HULL_SCALE_MILESTONES.length - 1];
  if (first === undefined || last === undefined) {
    return 0n;
  }
  if (targetBps <= first[1]) {
    return first[0];
  }
  if (targetBps >= HULL_MAX_SCALE_BPS) {
    return last[0];
  }
  for (let index = 1; index < HULL_SCALE_MILESTONES.length; index += 1) {
    const low = HULL_SCALE_MILESTONES[index - 1];
    const high = HULL_SCALE_MILESTONES[index];
    if (low === undefined || high === undefined) {
      continue;
    }
    if (targetBps <= high[1]) {
      const span = high[1] - low[1];
      const over = targetBps - low[1];
      /* Arredonda PARA CIMA: a vida devolvida tem de chegar mesmo ao alvo. */
      const health = low[0] + ((high[0] - low[0]) * over + span - 1n) / span;
      return hullScaleBps(health) >= targetBps ? health : health + 1n;
    }
  }
  return last[0];
}

/**
 * O raio de um casco em unidades INTEIRAS, dada a escala. O duelo.
 *
 * Trunca uma vez, no fim. `PVP_SHIP_RADIUS_MU x 10 000 / 10 000` é o raio de
 * sempre, sem divisão que arredonde - a identidade de novo.
 */
export function hullRadiusFromScaleBps(baseRadius: number, scaleBps: bigint): number {
  if (!Number.isSafeInteger(baseRadius) || baseRadius <= 0) {
    throw new RangeError(`a base hull radius is a positive whole number, got ${baseRadius}`);
  }
  if (scaleBps === BPS_ONE) {
    return baseRadius;
  }
  return Number((BigInt(baseRadius) * scaleBps) / BPS_ONE);
}

/**
 * A mesma escala como FATOR, para uma geometria fracionária e para o renderer.
 *
 * `1` exatamente na nave de referência, o que mantém o duelo sem joias byte a
 * byte no tamanho que já tinha.
 *
 * Quem desenha usa isto e NUNCA recalcula a curva: o número que dimensiona o
 * sprite é o mesmo que dimensiona a hitbox, e é por isso que a aparência não
 * pode crescer sem a física.
 */
export function hullScaleFactor(scaleBps: bigint): number {
  return scaleBps === BPS_ONE ? 1 : Number(scaleBps) / Number(BPS_ONE);
}
